import React, { useState, useEffect } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
  UIFormRow,
  UIFormLabels,
  UIFormFields,
  UIFormLabel
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UISelect from '@/components/shared/UISelect';
import UIRadioButton from '@/components/shared/UIRadioButton';
import UICheckbox from '@/components/shared/UICheckbox';
import UIButton from '@/components/shared/UIButton';
import { useToast } from '@/components/shared/ToastProvider';
import './PreferencesSection.scss';

export interface PreferencesSectionProps {
  /**
   * Початкові значення форми
   */
  initialValues?: {
    language?: string;
    timeZone?: string;
    dateFormat?: string;
    defaultSetting?: string;
    options?: string[];
  };

  /**
   * Обробник збереження
   */
  onSave?: (values: any) => void;

  /**
   * Обробник скидання
   */
  onClear?: () => void;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

// Моковые данные
const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'uk', label: 'Українська' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
];

const timeZoneOptions = [
  { value: 'gmt+5:30', label: 'GMT +5:30' },
  { value: 'gmt+2', label: 'GMT +2:00' },
  { value: 'gmt-5', label: 'GMT -5:00' },
  { value: 'gmt+8', label: 'GMT +8:00' },
];

const dateFormatOptions = [
  { value: 'dd/mm/yyyy', label: 'DD/MM/YYYY' },
  { value: 'mm/dd/yyyy', label: 'MM/DD/YYYY' },
  { value: 'yyyy-mm-dd', label: 'YYYY-MM-DD' },
  { value: 'no-preference', label: 'No Preference' },
];

const baseClass = 'PreferencesSection';
const PREFERENCES_KEY = 'userPreferences';

const PreferencesSection: React.FC<PreferencesSectionProps> = ({
                                                                 initialValues,
                                                                 onSave,
                                                                 onClear,
                                                                 loading = false,
                                                                 className = '',
                                                               }) => {
  const { showSuccess } = useToast();
  // Стан форми
  const [formData, setFormData] = useState({
    language: initialValues?.language || '',
    timeZone: initialValues?.timeZone || '',
    dateFormat: initialValues?.dateFormat || '',
    defaultSetting: initialValues?.defaultSetting || 'off',
    options: initialValues?.options || ['option3'],
  });

  useEffect(() => {
    const savedPreferences = localStorage.getItem(PREFERENCES_KEY);
    if (savedPreferences) {
      try {
        setFormData(JSON.parse(savedPreferences));
      } catch (e) {
        console.error("Error parsing preferences from localStorage", e);
      }
    }
  }, []);

  // Обробники змін
  const handleSelectChange = (field: string) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      defaultSetting: event.target.value,
    }));
  };

  const handleCheckboxChange = (optionValue: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setFormData(prev => ({
      ...prev,
      options: isChecked
        ? [...prev.options, optionValue]
        : prev.options.filter(opt => opt !== optionValue),
    }));
  };

  const handleSave = () => {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(formData));
      onSave?.(formData);
      showSuccess('Preferences saved successfully!');
      console.log('Preferences saved to localStorage', formData);
    } catch (e) {
      console.error("Error saving preferences to localStorage", e);
    }
  };

  const handleClear = () => {
    try {
      localStorage.removeItem(PREFERENCES_KEY);
      const defaultState = {
        language: '',
        timeZone: '',
        dateFormat: '',
        defaultSetting: 'off',
        options: [],
      };
      setFormData(defaultState);
      onClear?.();
      console.log('Preferences cleared from localStorage and state');
    } catch (e) {
      console.error("Error clearing preferences from localStorage", e);
    }
  };

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="form-horizontal"
      formPadding="medium"
      labelWidth="133px"
      formGap="24px"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Preferences"
          description="Preferences settings affect your view of the entire application."
        />
      </UISectionHeader>

      <UISectionBody>
        <UIFormRow>
          <UIFormLabels>
            <UIFormLabel>Language</UIFormLabel>
            <UIFormLabel>Time Zone</UIFormLabel>
            <UIFormLabel>Date Format</UIFormLabel>
            {/* <UIFormLabel>Default</UIFormLabel> */}
            {/* <UIFormLabel>Preferences options</UIFormLabel> */}
          </UIFormLabels>

          <UIFormFields>
            {/* Language Select */}
            <UISelect
              value={formData.language}
              options={languageOptions}
              placeholder="Select Option"
              onChange={handleSelectChange('language')}
              fullWidth
            />

            {/* Time Zone Select */}
            <UISelect
              value={formData.timeZone}
              options={timeZoneOptions}
              placeholder="Select Option"
              onChange={handleSelectChange('timeZone')}
              fullWidth
            />

            {/* Date Format Select */}
            <UISelect
              value={formData.dateFormat}
              options={dateFormatOptions}
              placeholder="Select Option"
              onChange={handleSelectChange('dateFormat')}
              fullWidth
            />

            {/* Default Radio Buttons */}
            {/*
            <div className={`${baseClass}__radio-group`}>
              <UIRadioButton
                name="defaultSetting"
                value="on"
                label="On"
                checked={formData.defaultSetting === 'on'}
                onChange={handleRadioChange}
              />
              <UIRadioButton
                name="defaultSetting"
                value="off"
                label="Off"
                checked={formData.defaultSetting === 'off'}
                onChange={handleRadioChange}
              />
            </div>
            */}

            {/* Preferences Options Checkboxes */}
            {/*
            <div className={`${baseClass}__checkbox-group`}>
              <UICheckbox
                value="option1"
                label="Option 1"
                checked={formData.options.includes('option1')}
                onChange={handleCheckboxChange('option1')}
              />
              <UICheckbox
                value="option2"
                label="Option 2"
                checked={formData.options.includes('option2')}
                onChange={handleCheckboxChange('option2')}
              />
              <UICheckbox
                value="option3"
                label="Option 3"
                checked={formData.options.includes('option3')}
                onChange={handleCheckboxChange('option3')}
              />
            </div>
            */}
          </UIFormFields>
        </UIFormRow>
      </UISectionBody>

      <UISectionFooter>
        <div className={`${baseClass}__buttons`}>
          <UIButton variant="primary" onClick={handleSave}>
            Save
          </UIButton>
          <UIButton variant="primary" outline onClick={handleClear}>
            Clear
          </UIButton>
        </div>
      </UISectionFooter>
    </UISectionWrapper>
  );
};

export default PreferencesSection;
